import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet-async';

import { Add, ArrowUpward, ArrowDownward, Close } from '@mui/icons-material';
import { Navigate } from 'react-router-dom';
import { Fab, Tooltip } from "@mui/material";

import Grid from '@mui/material/Grid2';

import { 
  FeatureSource,
  ModelBase, 
  PlayerObject,
  SubscriptionProps,
  UpsertableBase,
} from "@models";
import { 
  Campaign,
  Character,
  SourceBook, 
} from "@dndModels";
import { DATA_TYPE_INFO, FEATURE_SOURCE_TYPES } from "@dndModels/Constants";
import { DataType, isPlayerObjectType, SourceBookType } from "@dndModels/Types";

import MyTTRPGAPI from "@utilities/smart_api";

import CheckBox from "@components/input/CheckBox";
import LoadingBox from "@components/input/LoadingBox";
import { useDataTypeLoadProgress } from "@utilities/useApiLoadStatus";
import ObjectIndexItem from "@components/Navigation/ObjectIndexItem";

import ProgressBar from "@components/input/ProgressBar";
import SelectSourceBookBox from "@dndComponents/select/SelectSourceBookBox";
import AddSourceBookTool from "@dndComponents/AddSourceBookTool";
import DeleteSourceBookOnlyTool from "@dndComponents/DeleteSourceBookOnlyTool";
import UnownedHomebrewTool from "@dndComponents/UnownedHomebrewTool";
import CreateFirstSourceBookModal from "@dndComponents/CreateFirstSourceBookModal";
import FilterBox from "@dndComponents/filtering/FilterBox";
import LetterLinks from "@components/Navigation/LetterLinks";
import PageLinks from "@components/Navigation/PageLinks";
import DisplayStringWithReplacements from "@dndComponents/display/DisplayStringWithReplacements";
import InfoButton from "@components/input/buttons/InfoButton";
import { useAuth } from 'react-oidc-context';
import FlexBox from '@components/layout/FlexBox';
import DandDButtonBox from '@dndComponents/DandDButtonBoxSimple';

interface Props { 
  data_type: DataType;
  true_filter?: any;
  extra_filter?: (obj: UpsertableBase) => boolean;
  feature_source_type?: string;
  exclude_options?: string[];
  details?: boolean;
  special_copy?: (obj: UpsertableBase) => void;
  special_update?: (obj: UpsertableBase, onSuccess: () => void) => void;
  extra?: (obj: UpsertableBase) => React.ReactNode;
  extra_details?: (obj: UpsertableBase) => string;
  extra_buttons?: (obj: UpsertableBase) => React.ReactNode;
  name_display?: (obj: UpsertableBase) => React.ReactNode;
  disabled?: boolean;
  constants?: any;
  show_image?: boolean;
  save_all?: boolean;
  log_it?: boolean;
  empty_features?: boolean;
  onDelete?: (_id: string) => void;
  extra_by_add?: () => React.ReactNode;
  onAdd?: () => void;
  quick_fields?: string[];
  get_filtered?: (filtered: string[]) => void;
}

// const api = API.getInstance();
// const common_util = DataUtilities.getInstance();
const api = MyTTRPGAPI.getInstance();
// const char_util = MyTTRPGCharacterUtilities.getInstance();

export default function ObjectIndex({
  data_type,
  feature_source_type = "",
  exclude_options = [],
  details = false,
  true_filter = {},
  extra_filter,
  special_copy,
  special_update,
  // no_description = false,
  extra,
  extra_details,
  extra_buttons,
  name_display,
  disabled = false,
  constants = {},
  show_image = false,
  save_all = false,
  log_it = false,
  empty_features = false,
  onDelete,
  extra_by_add,
  quick_fields,
  onAdd,
  get_filtered
}: Props) {
  const auth = useAuth();
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loading_archive, setLoadingArchive] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [loading_message, setLoadingMessage] = useState("");
  const [percentage, setPercentage] = useState(0);
  // When this page started loading its data type - loads that finished before
  // then don't count toward its loading bar (see useDataTypeLoadProgress).
  const [load_since, setLoadSince] = useState(() => Date.now());
  const [archives, setArchives] = useState(false);
  const [archived_sb_id, setArchivedSBId] = useState(api.working_on_sb ? api.working_on_sb._id : "");
  const [archived_sb_type, setArchivedSBType] = useState<SourceBookType | "">(api.working_on_sb ? api.working_on_sb.source_book_type : "");
  const [page_num, setPageNum] = useState(0);
  const [objects, setObjects] = useState<(UpsertableBase)[]>([]);
  const [filtered, setFiltered] = useState<(UpsertableBase)[]>([]);
  const [archived_objects, setArchivedObjects] = useState<UpsertableBase[]>([]);
  const [saving_all, setSavingAll] = useState(false);
  const [saved, setSaved] = useState<string[]>([]);
  const [navigateTo, setNavigateTo] = useState<string | null>(null);
  const [filter, setFilter] = useState<any>({});
  const [filtering, setFiltering] = useState(false);
  const [sort_field, setSortField] = useState<string>("name");
  const [sort_descending, setSortDescending] = useState(false);
  const [display_info, setDisplayInfo] = useState(api.user ? !api.user.hide_info.includes(data_type) : true);
  const [reload_check, setReloadCheck] = useState(false);
  const [my_things_check, setMyThingsCheck] = useState(false);
  // What to do once a non-admin has made their first source book (Create New or Copy).
  const [after_source_book, setAfterSourceBook] = useState<(() => void) | null>(null);
  const [info_check, setInfoCheck] = useState(false);
  const prev_data_type = useRef<DataType | "">("");
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);
  
  const load = useCallback(async () => {
    if (log_it) {
      debugger;
    }
    if (isPlayerObjectType(data_type)) {
      if (api.user) { // No Point trying to load player objects if they're not logged in.
        let new_objects: PlayerObject[] = [];
        if (data_type === "campaign") {
          new_objects = [...api.user.my_campaigns];
        } else if (data_type === "character") {
          new_objects = [...api.user.my_characters];
        }
        if (new_objects.length === 0) {
          const onProgress = (data_type: DataType, percent: number) => {
            setPercentage(percent * 100);
            setLoadingMessage(`Loading ${dt_info?.plural??""}`);
          };
          // console.log(true_filter);
          const res = await api.getPlayerObjects({ 
            data_type, filter, onProgress
          });
          new_objects = [...res];
          // It will only come here if api.user should be storing these, so store them.
          if (data_type === "campaign") {
            const campaigns = new_objects as Campaign[];
            api.user.owned_campaigns = campaigns.filter(o => o.owner_id === api.user_id);
            api.user.playing_campaigns = campaigns.filter(o => o.owner_id !== api.user_id);
          } else if (data_type === "character") {
            api.user.my_characters = [...(new_objects as Character[])];
          }
        }
        if (extra_filter) {
          // console.log("extra filter");
          new_objects = new_objects.filter(o => extra_filter(o));
        }
        setObjects(new_objects.sort((a, b) => { return a.name.localeCompare(b.name); }));
        setLoading(false);
        setRefreshing(true);
      } else {
        setTimeout(() => {
          console.error("No User");
          setLoading(false);
        }, 100);
      }
    } else if (data_type === "user") {
      if (api.true_user && api.true_user.is_admin) { // Only admins are allowed to view the user list.
        console.log(true_filter);
        const res = await api.getUsers({ ... true_filter });
        let new_objects = [...res];
        // if (extra_filter) {
        //   // console.log("extra filter");
        //   new_objects = new_objects.filter(o => extra_filter(o));
        // }
        setObjects(new_objects.sort((a, b) => { return a.name.localeCompare(b.name); }));
        setLoading(false);
        setRefreshing(true);
      } else {
        setTimeout(() => {
          console.error("No User");
          setLoading(false);
        }, 100);
      }
    } else {
      // debugger;
      const onComplete = (res: ModelBase[]) => {
        let new_objects = [...res];
        if (extra_filter) {
          // console.log("extra filter");
          new_objects = new_objects.filter(o => extra_filter(o));
        }
        // console.log(new_objects);
        setObjects(new_objects.sort((a, b) => { return a.name.localeCompare(b.name); }));
        setLoading(false);
        setRefreshing(true);
      };
      const onProgress = (data_type: DataType, percent: number) => {
        setPercentage(percent * 100);
        setLoadingMessage(`Loading ${dt_info?.plural??""}`);
      };
      const onError = (message: string) => {
        setPercentage(0);
        setLoadingMessage(message);
      };
      const res = await api.getObjectsWithCallbacks<ModelBase>({ 
        data_type, 
        filter: (feature_source_type === "" ? { ...true_filter } : { ...true_filter, feature_source_type }),
        onProgress, onError, log_it
      });
      onComplete(res);
    }
  }, [data_type, api.user]);

  useEffect(() => {
    if (reload_check) {
      setReloadCheck(false);
      // Non-admins don't see blocked source books, so compare against the ones they can see.
      const available = data_type === "source_book" ? api.source_books : api.getDataTypeFromHash(data_type);
      if (api.isDataTypeLoaded(data_type) && objects.length < available.length) {
        setLoading(true);
        setTimeout(() => {
          load();
        }, 100);
      }
    }
  }, [reload_check, objects, load]);

  // Once the user's things are loaded, what they can see (and what they own) may have changed.
  // If the list is still loading it waits for that to finish, then reloads.
  useEffect(() => {
    if (my_things_check && fetched && !loading) {
      setMyThingsCheck(false);
      setLoading(true);
      setTimeout(() => {
        load();
      }, 100);
    }
  }, [my_things_check, fetched, loading, load]);

  useEffect(() => {
    if (info_check) {
      setInfoCheck(false);
      setDisplayInfo(api.user ? !api.user.hide_info.includes(data_type) : true);
    }
  }, [info_check, api.user]);

  const dataTypeLoaded = (props: SubscriptionProps) => {
    // Only this page's data type finishing (not every other one loading in the background) can change the list.
    if (props.value === data_type) {
      setReloadCheck(true);
    }
  }

  const authenticated = () => {
    setInfoCheck(true);
  }

  const myThingsLoaded = () => {
    setMyThingsCheck(true);
  }

  useEffect(() => {
    console.log("Getting Ready");
    api.subscription_holder.subscribe("Data Type Loaded", mounted, dataTypeLoaded);
    api.subscription_holder.subscribe("Authenticated", mounted, authenticated);
    api.subscription_holder.subscribe("My Things Loaded", mounted, myThingsLoaded);
  }, []);

  const dt_info = DATA_TYPE_INFO.find(o => o.data_type === data_type);
  
  const extra_fields = useMemo(() => {
    return dt_info?.extra_fields || [];
  }, [dt_info]);

  const loadArchives = (id: string) => {
    if (!loading && api) {
      setLoadingArchive(true);
      if (log_it) {
        debugger;
      }
      const use_filter = { ...true_filter };
      api.getObjects({
        data_type, 
        filter: use_filter,
        ignore_templates: false, archives: [id]
      }).then(res => {
        let new_objects = res as ModelBase[];
        new_objects = new_objects.filter(o => o.source_book_ids.includes(id));
        if (extra_filter) {
          // console.log("extra filter");
          new_objects = new_objects.filter(o => extra_filter(o));
        }
        setArchivedObjects(new_objects.sort((a, b) => { return a.name.localeCompare(b.name); }));
        setLoadingArchive(false);
        setRefreshing(true);
      });
    }
  }

  // I'm working on making it so player objects are handled quite differently from other things.
  // I'm going to store them in the user similar to how the api hashes work. 
  // If api.user.admin then it can have access to other people's stuff, 
  // but to do that we normally will be spoofing them, 
  // in which case it will still put them on the user.
  // They can also be gotten through campaigns and through shared links.
  // For campaigns and shared links I want to use different api calls.
  // For everything else it will automatically get the ones that belong to the user, 
  // storing them all on the user like an api hash.
  useEffect(() => {
    if (data_type !== prev_data_type.current) {
      prev_data_type.current = data_type;
      setLoadSince(Date.now());
      setLoading(true);
      setFetched(true);
      // debugger;
      setTimeout(() => {
        load();
      }, 100);
    }
  }, [data_type, fetched]);

  // Averaged over this data type, its dependencies and both source book scopes,
  // so it doesn't jump between whichever load reported progress last.
  const load_progress = useDataTypeLoadProgress([data_type], load_since);

  const sorter = useCallback((a: any, b: any) => {
    let primary_sort = 0;
    
    // Handle favorite sorting for Campaigns
    if (a instanceof Campaign && b instanceof Campaign && sort_field === "name" && api.user) {
      let a_is_favorite = false;
      if (a.owner_id === api.user._id) {
        a_is_favorite = a.is_favorite;
      } else {
        const c_player = api.user.c_players.find(o => o.campaign_id === a._id);
        if (c_player) {
          a_is_favorite = c_player.campaign_is_favorite;
        }
      }
      let b_is_favorite = false;
      if (b.owner_id === api.user._id) {
        b_is_favorite = b.is_favorite;
      } else {
        const c_player = api.user.c_players.find(o => o.campaign_id === b._id);
        if (c_player) {
          b_is_favorite = c_player.campaign_is_favorite;
        }
      }
      if (a_is_favorite !== b_is_favorite) {
        return a_is_favorite ? -1 : 1;
      }
    }
    // Handle favorite sorting for PlayerObjects
    if (a instanceof PlayerObject && b instanceof PlayerObject && sort_field === "name") {
      if (a.is_favorite !== b.is_favorite) {
        return a.is_favorite ? -1 : 1;
      }
    }

    // Primary sort by the selected field
    if (sort_field === "name") {
      const a_name = a instanceof ModelBase ? a.display_format : a.name;
      const b_name = b instanceof ModelBase ? b.display_format : b.name;
      primary_sort = a_name.trim().localeCompare(b_name.trim());
    } else {
      // Sort by extra field
      const a_value: any = (a as any)[sort_field];
      const b_value: any = (b as any)[sort_field];
      
      // // If there's a display function, use it to get the sortable value
      // if (field_config?.display) {
      //   a_value = field_config.display(a);
      //   b_value = field_config.display(b);
      // }
      
      // Handle different types
      if (typeof a_value === 'number' && typeof b_value === 'number') {
        primary_sort = a_value - b_value;
      } else {
        const a_str = String(a_value || '');
        const b_str = String(b_value || '');
        primary_sort = a_str.localeCompare(b_str);
      }
    }

    // Apply descending if needed
    if (sort_descending) {
      primary_sort = -primary_sort;
    }

    // Secondary sort by name (ascending) if primary sort is equal and not sorting by name
    if (primary_sort === 0 && sort_field !== "name") {
      const a_name = a instanceof ModelBase ? a.display_format : a.name;
      const b_name = b instanceof ModelBase ? b.display_format : b.name;
      return a_name.trim().localeCompare(b_name.trim());
    }

    return primary_sort;
  }, [sort_field, sort_descending]);

  const refilter = useCallback(() => {
    // debugger;
    let new_filtered: (UpsertableBase)[] = []; 
    if (archives && data_type !== "source_book" && dt_info && !dt_info.player_object) {
      new_filtered = archived_objects; // .filter(o => o.source_book_ids.includes(archived_sb_id));
    } else {
      new_filtered = objects;
    }
    const the_filter = {...filter};
    if (the_filter.start_letter) {
      the_filter["name*startsWith"] = the_filter.start_letter;
      delete the_filter.start_letter;
    }
    if (log_it) {
      debugger;
    }
    // debugger;
    // console.log(new_filtered);
    // console.log(the_filter);
    new_filtered = api.filter(data_type, new_filtered, the_filter) as (UpsertableBase)[];
    // console.log(new_filtered);
    if (new_filtered.length > 0) {
      if (data_type !== "user" && (data_type === "source_book" || (dt_info && dt_info.player_object))) {
        new_filtered = (new_filtered as (ModelBase | PlayerObject)[]).filter(o => o.archived === archives);
      }
      new_filtered = new_filtered.sort(sorter);
    }
    if (empty_features) {
      if (FEATURE_SOURCE_TYPES.includes(data_type)) {
        const feature_sources = new_filtered as FeatureSource[];
        new_filtered = feature_sources.filter(o => o.feature_bases.filter(o2 => o2.features.filter(o => o.floating_feature === null).length > 0).length > 0);
      } else if (data_type === "creature") {
        // const creatures = new_filtered as Creature[];
        // new_filtered = creatures.filter(o => 
        //   o.action_fbs.filter(o2 => o2.features.filter(o => o.floating_feature === null).length > 0).length > 0 ||
        //   o.legendary_action_fbs.filter(o2 => o2.features.filter(o => o.floating_feature === null).length > 0).length > 0 ||
        //   o.lair_action_fbs.filter(o2 => o2.features.filter(o => o.floating_feature === null).length > 0).length > 0 ||
        //   o.traits.filter(o2 => o2.features.filter(o => o.floating_feature === null).length > 0).length > 0
        // );
        // new_filtered = creatures.filter(o => o.actions.filter(o => o.floating_feature === null).length > 0 || o.legendary_actions.filter(o => o.floating_feature === null).length > 0 || o.special_abilities.filter(o => o.floating_feature === null).length > 0);
      }
    }
    setFiltered(new_filtered);
    if (get_filtered) {
      get_filtered(new_filtered.map(o => o._id));
    }
  }, [archives, archived_objects, data_type, empty_features, filter, dt_info, get_filtered, log_it, objects, sorter]);

  useEffect(() => {
    if (refreshing) {
      setRefreshing(false);
      refilter();
    }
  }, [refreshing, refilter]);

  useEffect(() => {
    if (filtering) {
      refilter();
      setFiltering(false);
    }
  }, [filtering, refilter]);

  const refresh = () => {
    setRefreshing(true);
  }

  useEffect(() => {
    if (saved.length < filtered.length) {
      saveNext();
    } else {
      setSavingAll(false);
    }
    // eslint-disable-next-line
  }, [saved]);

  const saveNext = async () => {
    if (saving_all) {
      const saveMe = filtered.filter(o => !saved.includes(o._id))[0];
      if (saveMe) {
        if (saveMe instanceof ModelBase) {
          await api.loadObject(saveMe);
        }
        api.updateObject(data_type, saveMe).then(res => {
          const new_saved = [...saved];
          new_saved.push(saveMe._id);
          setSaved(new_saved);
        });
      } else {
        setSavingAll(true);
      }
    }
  }

  const saveAll = async () => {
    debugger;
    if (saving_all) {
      setSavingAll(false);
    } else {
      const saveMe = filtered[0];
      if (saveMe) {
        setSavingAll(true);
        if (saveMe instanceof ModelBase) {
          await api.loadObject(saveMe);
        }
        await api.updateObject(data_type, saveMe)
        setSaved([saveMe._id]);
      }
    }
  }

  const renderSavingAll = () => {
    if (saving_all) {
      return (
        <FlexBox className="padded">
          <ProgressBar value={saved.length} max={filtered.length} color='Green' display_progress="counts" />
        </FlexBox>
      );
    }
    return null;
  }

  const updateFilter = (the_filter: any, startLetter = "") => {
    // debugger;
    const new_filter = {...the_filter};
    
    if (startLetter !== "") {
      new_filter["name*startsWith"] = startLetter;
    } else {
      delete new_filter["name*startsWith"];
    }

    setPageNum(0);
    setFilter(new_filter);
    setFiltering(true);
  }

  const handleHeaderClick = (field: string) => {
    if (sort_field === field) {
      // Toggle descending for the same field
      setSortDescending(!sort_descending);
    } else {
      // New field, start with ascending
      setSortField(field);
      setSortDescending(false);
    }
    setRefreshing(true);
  }

  const renderColumnHeaders = () => {
    if (filtered.length === 0) {
      return null;
    }

    // debugger;
    const hasExtraFields = extra_fields.length > 0;
    const nameSize = hasExtraFields ? 12 - (extra_fields.length * 2) : 12;

    return (
      <FlexBox>
        <Grid container spacing={0} direction="row" className="tableHeader" style={{ marginBottom: '4px' }}>
          <Grid
            className="padded clickable"
            onClick={() => handleHeaderClick("name")}
            size={{
              xs: nameSize
            }}>
            Name
            {sort_field === "name" && (
              sort_descending ? <ArrowDownward fontSize="small" /> : <ArrowUpward fontSize="small" />
            )}
          </Grid>
          {extra_fields.map((field, index) => {
            return (
              <Grid size={2} key={index}
                className="padded clickable centered"
                onClick={() => handleHeaderClick(field.field)}>
                {field.label}
                {sort_field === field.field && (
                  sort_descending ? <ArrowDownward fontSize="small" /> : <ArrowUpward fontSize="small" />
                )}
              </Grid>
            );
          })}
        </Grid>
      </FlexBox>
    );
  }

  const toggleInfo = () => {
    const user = api.user;
    if (user) {
      if (display_info) {
        user.hide_info.push(data_type);
      } else {
        user.hide_info = user.hide_info.filter(o => o !== data_type);
      }

      api.upsertObject("user", user);
    }
    setDisplayInfo(!display_info);
  }

  const requireSourceBook = (then: () => void) => {
    setAfterSourceBook(() => then);
  }

  const startAdd = () => {
    if (onAdd) {
      onAdd();
    } else {
      setNavigateTo(`/${data_type}/create`);
    }
  }

  const render = () => {
    // debugger;
    if (!fetched || loading || auth.isLoading) {
      // Player objects don't go through the load status store, so they still use onProgress.
      const shown_percentage = load_progress !== undefined && !isPlayerObjectType(data_type) ? load_progress * 100 : percentage;
      return (<LoadingBox message={loading_message} percentage={shown_percentage} size={window.innerHeight * 0.5} height={window.innerHeight - 64} width={window.innerWidth - 24} />); // width={window.innerWidth - 17}
    } else if (navigateTo !== null) {
      return <Navigate to={navigateTo} />;
    } else if (!dt_info) {
      return <>Invalid Data Type</>;
    } else {
      const height = window.innerHeight - 221;
      const page_size = window.innerWidth >= 1000 ? Math.floor(height / 36.5) : 10;

      // if (data_type === "floating_feature" && filtered[0] instanceof FloatingFeature) {
      //   console.log((filtered as FloatingFeature[]).map(o => o.feature_type));
      // }
      // console.log(objects);
      
      const page_count = Math.ceil(filtered.length / page_size);
      const filtered_and_paged = filtered.slice(page_size * page_num, page_size * (page_num + 1));

      // console.log(filtered);
      return (
        <FlexBox direction="column">
          <Helmet>
            <title>Homebrew Game Labs - { dt_info.plural }</title>
          </Helmet>
          { api.online && api.user && api.user.admin && !dt_info.player_object &&
            data_type !== "user" && data_type !== "source_book" &&
            <AddSourceBookTool data_type={data_type} plural={dt_info.plural}
              objects={filtered.filter((o): o is ModelBase => o instanceof ModelBase)}
              onDone={refresh}
            />
          }
          { api.online && api.user && api.user.admin && !dt_info.player_object &&
            data_type !== "user" && data_type !== "source_book" &&
            <UnownedHomebrewTool data_type={data_type} plural={dt_info.plural} onDone={refresh} />
          }
          { api.online && api.user && api.user.admin && !dt_info.player_object &&
            data_type !== "user" && data_type !== "source_book" &&
            <DeleteSourceBookOnlyTool data_type={data_type} plural={dt_info.plural}
              objects={filtered.filter((o): o is ModelBase => o instanceof ModelBase)}
              onDeleted={(ids: string[]) => {
                if (onDelete) {
                  ids.forEach(id => onDelete(id));
                }
                setObjects(prev => prev.filter(o => !ids.includes(o._id)));
                setRefreshing(true);
              }}
            />
          }
          <FlexBox direction="row">
            <FlexBox xs_expand={0}>
              <span className="header padded">
                { dt_info.plural }
                { dt_info.info &&
                  <InfoButton onClick={toggleInfo} />
                }
              </span>
              { !exclude_options.includes("Add") && api.user && api.online && 
                <Tooltip title={`Create New ${dt_info.name}`}>
                  <Fab size="small" color="primary" style={{marginLeft: "8px"}}
                    onClick={ () => {
                      // Non-admins can only create things in their own source books.
                      if (api.user && !api.user.admin && api.dataTypeUsesSourceBooks(data_type) && 
                        api.owned_source_books.length === 0) {
                        requireSourceBook(startAdd);
                      } else {
                        startAdd();
                      }
                    }}>
                    <Add/>
                  </Fab>
                </Tooltip> 
              }
              { api.user && extra_by_add && extra_by_add() }
              { after_source_book &&
                <CreateFirstSourceBookModal
                  open={after_source_book !== null}
                  item_name={dt_info.name}
                  onClose={() => setAfterSourceBook(null)}
                  onCreated={() => {
                    const then = after_source_book;
                    setAfterSourceBook(null);
                    then();
                  }}
                />
              }
            </FlexBox>
            <FlexBox className="padded">
              { !loading && 
                <FilterBox 
                  data_type={data_type}
                  onChange={updateFilter}
                  onLoad={updateFilter}
                  // log_it
                  all_objects={objects}
                  quick_fields={quick_fields}
                />
              }
            </FlexBox>
          </FlexBox>
          { dt_info.info && display_info &&
            <FlexBox direction="row" className="info-box">
              <FlexBox>
                <DisplayStringWithReplacements display_string={dt_info.info} />
              </FlexBox>
              <FlexBox xs_expand={0}>
                <div className="clickable float-right" onClick={toggleInfo}>
                  <Close />
                </div>
              </FlexBox>
            </FlexBox>
          }
          <FlexBox direction="column">
            <FlexBox xs_expand={0}>
              <CheckBox
                name="View Archives"
                value={archives}
                disabled={!api.online}
                onChange={() => {
                  setArchives(!archives);
                  setRefreshing(true);
                }}
              />
            </FlexBox>
            { archives && data_type !== "source_book" && !dt_info.player_object &&
              <FlexBox>
                <SelectSourceBookBox
                  name="Archived Source Book"
                  id={archived_sb_id}
                  type={archived_sb_type}
                  archives
                  onChange={(type, id) => {
                    setArchivedSBType(type);
                    const old_id = archived_sb_id;
                    setArchivedSBId(id);
                    if (id !== old_id) {
                      loadArchives(id);
                    }
                  }}
                />
              </FlexBox>
            }
          </FlexBox>
          { save_all && api.online && api.user && api.user.admin &&
            <>
              <FlexBox>
                <DandDButtonBox
                  name={ saving_all ? "Cancel" : "Save All" }
                  onClick={saveAll}
                />
              </FlexBox>
              { saving_all && 
                <FlexBox className="header centered">Saving All</FlexBox> 
              }
            </>
          }
          { saving_all ?
            renderSavingAll()
          :
            <>
              { loading_archive ? 
                <FlexBox>
                  <LoadingBox />
                </FlexBox>
              :
                <>
                  <FlexBox>
                    <PageLinks selected={page_num} page_count={page_count} onChange={setPageNum} />
                  </FlexBox>
                  { renderColumnHeaders() }
                  <FlexBox>
                    <FlexBox direction="column">
                      { !saving_all && !refreshing && filtered_and_paged.map((obj) => {
                        return (
                          <FlexBox key={obj._id}>
                            <ObjectIndexItem 
                              obj={obj} 
                              data_type={data_type}
                              disabled={processing || disabled}
                              onProcessingChange={setProcessing}
                              refresh={refresh}
                              // alternate={ key % 2 === 0 }
                              constants={constants}
                              onDelete={(_id: string) => {
                                const new_objects = objects.filter(o => o._id !== _id);
                                if (onDelete) {
                                  onDelete(_id);
                                }
                                setObjects(new_objects);
                                setRefreshing(true);
                              }}
                              special_copy={special_copy}
                              requireSourceBook={requireSourceBook}
                              special_update={special_update}
                              feature_source_type={feature_source_type}
                              no_edit={exclude_options.includes("Edit")}
                              details={details}
                              show_image={show_image}
                              extra={extra}
                              extra_details={extra_details}
                              extra_buttons={extra_buttons}
                              log_it={log_it}
                              name_display={name_display}
                              extra_fields={extra_fields}
                              onArchiveChange={() => {
                                if (obj instanceof PlayerObject || obj instanceof SourceBook) {
                                  if (obj.is_archived) {
                                    const new_objects = objects.filter(o => o._id !== obj._id);
                                    setObjects(new_objects);
                                  } else {
                                    let new_objects = archived_objects.filter(o => o._id !== obj._id);
                                    setArchivedObjects(new_objects);
                                    new_objects = [...(objects as ModelBase[])];
                                    new_objects.push(obj as ModelBase);
                                    setObjects(new_objects);
                                  }
                                  setRefreshing(true);
                                }
                              }}
                            />
                          </FlexBox>
                        );
                      })}
                    </FlexBox>
                  </FlexBox>
                  <FlexBox>
                    <PageLinks selected={page_num} page_count={page_count} onChange={setPageNum} />
                  </FlexBox>
                  <FlexBox>
                    <LetterLinks onChange={(changed: string) => {
                      updateFilter(filter, changed);
                    }} />
                  </FlexBox>
                </>
              }
            </>
          }
        </FlexBox>
      ); 
    } 
  }

  return render();
}